import {formatDistanceToNow, parseISO} from 'date-fns'
import React, {Suspense, useEffect} from 'react'
import {graphql, useFragment} from 'react-relay'

import {subscribeOnBuild} from '../../../../../../actions/builds'
import {useRunningBuildUpdaterRelay} from '../../../../../../hooks/relay/useRunningBuildUpdaterRelay'
import Routes, {getHref} from '../../../../../../routes'
import BuildDurationAnchor from '../../../../../common/BuildDuration/BuildDurationAnchor/BuildDurationAnchor.container'
import RouterLink from '../../../../../common/RouterLink/RouterLink'
import StopBuild from '../../../../../common/StopBuild/StopBuild.container'
import BuildChangesDropdown from '../../../../../packages/Changes/ChangesDropdown/BuildChangesDropdown.container'
import PipelineEditButton from '../../../components/common/PipelineEditButton/PipelineEditButton'
import RunButton from '../../../components/common/RunButton/RunButton'
import {RunStatusRelay} from '../../../components/common/RunStatus/RunStatus.relay'
import {usePipelineHeadId} from '../../../hooks/pipeline'
import {pipelinesApi} from '../../../services/pipelinesApi'

import type {PipelineLineProjectFragment$key} from './__generated__/PipelineLineProjectFragment.graphql'
import {useLastRun} from './PipelineLine.hooks'

import styles from './PipelineLine.css'

import {useAppDispatch} from 'src/hooks/react-redux'

type Props = {
  pipelineId: string
  index: number
  projectKey: PipelineLineProjectFragment$key | null
}

const ROWS_IN_LINE = 3

const fragment = graphql`
  fragment PipelineLineProjectFragment on ProjectRest {
    ...pipelineHeadFragment
    ...PipelineLineLastRunFragment
  }
`

export function PipelineLine({pipelineId, index, projectKey}: Props) {
  const {name} = pipelinesApi.endpoints.getAllPipelines.useQuery(undefined, {
    selectFromResult: ({data}) => ({
      name: data?.find(item => item.id === pipelineId)?.settings.name,
    }),
  })
  const project = useFragment(fragment, projectKey)
  const headId = usePipelineHeadId(project)
  const run = useLastRun(project)
  const {rawId, state, startEstimate, finishDate} = run ?? {}
  const dispatch = useAppDispatch()
  useEffect(() => (rawId != null ? dispatch(subscribeOnBuild(rawId)) : undefined))
  useRunningBuildUpdaterRelay(run)
  return (
    <>
      {index > 0 && <div className={styles.gap} />}
      <div className={styles.row}>
        <div className={styles.hoverRow} style={{gridRowStart: index * ROWS_IN_LINE + 1}} />
        <div className={styles.name}>
          <RouterLink className={styles.nameLink} to={getHref(Routes.PIPELINE, {id: pipelineId})}>
            {name}
          </RouterLink>
        </div>
        <div>{run == null && <span className={styles.noRuns}>{'No finished runs'}</span>}</div>
        <div />
        <div className={styles.actions}>
          <Suspense>
            <PipelineEditButton to={getHref(Routes.EDIT_PIPELINE, {id: pipelineId})} />
          </Suspense>
          <RunButton pipelineId={pipelineId} className={styles.button} headId={headId!} />
        </div>
      </div>
      {run != null ? (
        <div className={styles.row}>
          <div className={styles.hoverRow} style={{gridRowStart: index * ROWS_IN_LINE + 2}} />
          <div className={styles.status}>
            <RunStatusRelay runKey={run} pipelineId={pipelineId} />
          </div>
          <div>
            <BuildChangesDropdown buildId={rawId!} />
          </div>
          <div className={styles.actions}>
            {state === 'queued' ? (
              startEstimate != null ? (
                <span className={styles.estimate}>
                  {formatDistanceToNow(parseISO(startEstimate), {addSuffix: true})}
                </span>
              ) : null
            ) : state === 'running' ? (
              <>
                <BuildDurationAnchor buildId={rawId!} />
                <StopBuild className={styles.stop} buildId={rawId} />
              </>
            ) : finishDate != null ? (
              formatDistanceToNow(parseISO(finishDate), {addSuffix: true})
            ) : null}
          </div>
        </div>
      ) : (
        <div className={styles.emptyRow} />
      )}
    </>
  )
}
